import { action, computed, observable } from 'mobx';
import last from 'lodash/last';
import { ContainerableBlock } from '~/models/blocks/containerable-block.model';
import {
    IServiceSelectionBlockResponse,
    ISessionConfig,
    TServiceSelectionBlockType
} from '~/types/server.types';
import {
    ServiceSelectionType,
    ServicesItemsLayoutType
} from '~/types/model.types';
import { CONTENT_BLOCK_TYPES, DnDTypes, TBlock } from '~/types/builder.types';
import type { FlowRevision } from '~/models/flow-revision.model';
import type { RootStore } from '~/stores/root.store';
import { IServiceSelectionBlockStyles } from '~/types/block-styles.types';
import { ISelectedServiceValue } from '~/components/ClientExperience/OneFlow/FlowContent/CXServiceSelectionBlock/CXServiceSelection.types';
import type { ServiceItemsContainerBlock } from '~/models/blocks/service-items-container-block.model';
import { ServiceSelectionItemBlock } from '~/models/blocks/service-selection-item-block.model';
import { InvoiceBlock } from '~/models/blocks/invoice-block.model';
import { MobileContainerName } from '~/components/Builder/Block/ServicesBlock/Mobile/MobileInvoice.constants';
import { ToastType } from '@honeybook/hbui/dist/components/HBToast/HBToast.types';
import { i18n } from '~/i18n/config';
import { NAMESPACES } from '~/i18n/namespaces';

type ServiceSelectionBlockDataForServer = ReturnType<
    ContainerableBlock['getDataForServer']
> &
    Pick<
        IServiceSelectionBlockResponse,
        | 'type'
        | 'invoice_block_id'
        | 'selection_required'
        | 'send_info_to_invoice'
        | 'selection_type'
        | 'show_total'
        | 'discount'
        | 'services_layout'
        | 'banner_text'
        | 'service_selection_type'
    >;

export class ServiceSelectionBlock extends ContainerableBlock {
    type: 'service_selection_block';
    @observable invoice_block_id: string;
    @observable selection_required: boolean;
    @observable send_info_to_invoice: boolean;
    @observable selection_type: ServiceSelectionType;
    @observable service_selection_type: TServiceSelectionBlockType;
    @observable show_total: boolean;
    @observable discount: boolean;
    @observable services_layout: ServicesItemsLayoutType;
    @observable banner_text: string;
    @observable styles: IServiceSelectionBlockStyles;

    constructor(
        block: IServiceSelectionBlockResponse,
        flowRevision: FlowRevision<RootStore>,
        rootStore: RootStore
    ) {
        super(block, flowRevision, rootStore);
        this.type = block.type;
        this.styles = {
            ...this.styles,
            ...block.service_selection_styles
        };
        this.connected_variable_id = block.connected_variable_id;
        this.send_info_to_invoice = block.send_info_to_invoice ?? false;
        this.banner_text = block.banner_text;
        this.services_layout =
            block.services_layout || ServicesItemsLayoutType.Rows;
        this.service_selection_type = block.service_selection_type;
        if (block.service_selection_type === 'session') {
            this.selection_required = true;
            this.selection_type = ServiceSelectionType.One;
        } else {
            this.selection_required = block.selection_required ?? true;
            this.selection_type =
                block.selection_type || ServiceSelectionType.Multiple;
        }
    }

    get isDeletable() {
        return true;
    }

    @computed
    get isAnyChildWithErrors() {
        const {
            builderUiStore: { showErrors, selectedFlow }
        } = this.rootStore;

        if (selectedFlow.is_template || !showErrors) {
            return false;
        }

        return this.allDescendants().some(child => child.errors.hasErrors);
    }

    get isResponsesActionBlock() {
        return this.isChargeable;
    }

    get isServiceSelectionItemBlock() {
        return false;
    }

    get isActionBlock() {
        return true;
    }

    get hoverable() {
        return false;
    }

    @computed
    get isColumnLayout() {
        return this.services_layout !== ServicesItemsLayoutType.Rows;
    }

    isDraggable() {
        return true;
    }

    @computed
    get shouldAddCompositionMenu() {
        return true;
    }

    @computed
    get dndType() {
        return DnDTypes.HorizontalBlock;
    }

    get displayName() {
        return 'service selection';
    }

    dndAcceptedTypes() {
        return DnDTypes.HorizontalBlock;
    }

    @computed
    get attachedInvoice() {
        return this.flowRevision.getBlock(
            this.invoice_block_id
        ) as InvoiceBlock;
    }

    @computed
    get isSomeItemsCustomQuantity() {
        return this.allDescendants()
            .filter(block => block instanceof ServiceSelectionItemBlock)
            .some(
                serviceItem =>
                    (serviceItem as ServiceSelectionItemBlock).isCustomQuantity
            );
    }

    @computed
    get hasNoUnitEconomicsShown() {
        return !this.styles.display_quantity && !this.styles.display_unit_price;
    }

    @computed
    get hasSelection() {
        return this.selection_type !== ServiceSelectionType.NoSelection;
    }

    @computed
    get isAutoSelection() {
        return !this.hasSelection && this.send_info_to_invoice;
    }

    @computed
    get isChargeable() {
        return this.hasSelection || this.send_info_to_invoice;
    }

    @computed
    get cogIconTestId() {
        return MobileContainerName.ServiceSelection;
    }

    @computed
    get serviceItems(): ServiceSelectionItemBlock[] {
        return this.childBlocks.reduce((serviceItems, childBlock) => {
            if (childBlock.isServiceItemsContainerBlock) {
                serviceItems.push(
                    ...(childBlock as ServiceItemsContainerBlock).childBlocks
                );
            }
            return serviceItems;
        }, []);
    }

    @computed
    get digitalProductServiceItems(): ServiceSelectionItemBlock[] {
        return this.serviceItems.filter(
            serviceItemBlock => serviceItemBlock.service_asset_enabled
        );
    }

    @computed
    get hasDigitalProductServiceItems(): boolean {
        return this.serviceItems.some(
            serviceItemBlock => serviceItemBlock.service_asset_enabled
        );
    }

    @computed
    get isEmpty() {
        return this.serviceItems.length === 0;
    }

    @computed
    get allServiceItems(): ServiceSelectionItemBlock[] {
        return this.serviceItems.concat(
            this.serviceItems.flatMap(
                x => x.childBlocks
            ) as ServiceSelectionItemBlock[]
        );
    }

    @computed
    get serviceItemsContainerBlocks() {
        return this.childBlocks.filter(
            childBlock => childBlock.isServiceItemsContainerBlock
        ) as ServiceItemsContainerBlock[];
    }

    @computed
    get lastServiceItemsContainerBlock() {
        return last(this.serviceItemsContainerBlocks);
    }

    @computed
    get numberOfServiceItems() {
        return {
            packagesNumber: this.serviceItems.filter(item => item.isPackage)
                .length,
            itemsNumber: this.serviceItems.filter(item => item.isService)
                .length,
            get hasBoth() {
                return !!this.packagesNumber && !!this.itemsNumber;
            }
        };
    }

    @computed
    get sortedServicesByMinPrice() {
        return this.serviceItems.sort(
            (serviceA, serviceB) =>
                serviceA.totalPriceIncludingSubItems -
                serviceB.totalPriceIncludingSubItems
        );
    }

    @computed
    get cheapestServiceItem() {
        return this.serviceItems
            .slice()
            .sort(
                (serviceA, serviceB) =>
                    serviceA.serviceMinPrice - serviceB.serviceMinPrice
            )[0];
    }

    @computed
    get sortedServicesByMaxPrice() {
        return this.serviceItems.sort(
            (serviceA, serviceB) =>
                serviceA.serviceMaxPrice - serviceB.serviceMaxPrice
        );
    }

    @computed
    get servicesPriceRange() {
        return {
            minServicesPrice:
                this.sortedServicesByMinPrice[0]?.totalPriceIncludingSubItems ||
                0,
            maxServicesPrice:
                last<ServiceSelectionItemBlock>(this.sortedServicesByMaxPrice)
                    ?.serviceMaxPrice === Infinity
                    ? '$∞'
                    : last<ServiceSelectionItemBlock>(
                          this.sortedServicesByMaxPrice
                      )?.serviceMaxPrice || 0
        };
    }

    @computed
    get selectedServiceBlocks() {
        if (!this.hasSelection && this.send_info_to_invoice) {
            return this.serviceItems;
        }

        const selectedItemsIds = (
            (this.connectedVariable?.value
                ?.selected_services as ISelectedServiceValue[]) || []
        ).map(item => item.service_item_block_id);
        return this.serviceItems.filter(item =>
            selectedItemsIds.includes(item.block_id)
        );
    }

    @computed
    get selectedServicesTotalPrice() {
        return this.selectedServiceBlocks.reduce((totalPrice, serviceBlock) => {
            return totalPrice + serviceBlock.totalPriceIncludingSubItems;
        }, 0);
    }

    @computed
    get isSelectionComplete() {
        if (
            this.selection_type === ServiceSelectionType.NoSelection ||
            !this.selection_required
        ) {
            return true;
        }
        return this.selectedServiceBlocks.length > 0;
    }

    @computed
    get isComplete() {
        // if it's no selection, then it's completed when someone viewed the page.
        if (!this.hasSelection) {
            return this.page.isPageViewed;
        }

        // if service is not required, and some client submitted the page then it's considered complete.
        // else, if we have any services selected it's considered complete.
        return !this.selection_required && this.page.submissions?.length
            ? true
            : this.selectedServiceBlocks.length > 0;
    }

    @computed
    get isAnyChildBlockSelected() {
        const { selectedBlockId } = this.rootStore.builderUiStore;
        return this.allDescendants().some(
            blk => blk.block_id === selectedBlockId
        );
    }

    @computed
    get isAnyServiceItemEditedForInvoice() {
        return this.allServiceItems.some(item => !!item?.invoice_service_item);
    }

    @computed
    get isSessionSelection() {
        return this.service_selection_type === 'session';
    }

    @computed
    get firstSelectedService(): ServiceSelectionItemBlock {
        const selected_services =
            this.connectedVariable?.value?.selected_services;

        if (selected_services && selected_services.length > 0) {
            return this.serviceItems.find(
                item =>
                    item.block_id === selected_services[0].service_item_block_id
            );
        }
    }

    @computed
    get sessionConfigs(): ISessionConfig[] {
        const sessionSelectionItems = this.serviceItems
            .filter(serviceItemBlock => !!serviceItemBlock.session_config)
            .map(serviceItemBlock => serviceItemBlock.session_config)
            .reduce((acc, item) => {
                acc[item._id] = item;
                return acc;
            }, {});
        return Object.values(sessionSelectionItems);
    }

    @action
    mergeBlockAttributes(block, attributes) {
        super.mergeBlockAttributes(block, attributes);
        if (
            'connected_variable_id' in attributes &&
            block.connected_variable_id
        ) {
            this.connected_variable_id = block.connected_variable_id;
        }

        if (block.invoice_block_id) {
            this.invoice_block_id = block.invoice_block_id;
        }
    }

    canBeParentOf(block: TBlock) {
        return (
            block.isServiceItemsContainerBlock ||
            CONTENT_BLOCK_TYPES.includes(block.type)
        );
    }

    override afterActionsApplied() {
        this.createSchedulerForSessionSelectionIfMissing();
    }

    private createSchedulerForSessionSelectionIfMissing() {
        if (this.service_selection_type !== 'session') {
            return;
        }

        if (this.flow.hasScheduleBlock) {
            const { builderUiStore, toastStore } = this.rootStore;
            const t = i18n.getFixedT(
                null,
                NAMESPACES.BUILDER,
                'service_selection.sidebar.select_type_popover.scheduler_updated_toast'
            );

            const toastId = this.block_id;

            toastStore.add({
                id: toastId,
                type: ToastType.Info,
                text: t('title'),
                autoDismissMs: 8000,
                actionLink: {
                    text: t('link'),
                    onClick: () =>
                        builderUiStore.selectAndScrollToBlock({
                            block: this.flow.scheduleContainerBlock,
                            focusBlock: true
                        })
                }
            });
        } else {
            const { pageCtrl, builderUiStore, toastStore } = this.rootStore;
            const toastId = this.block_id;
            const t = i18n.getFixedT(
                null,
                NAMESPACES.BUILDER,
                'service_selection.sidebar.select_type_popover.scheduler_created_toast'
            );

            pageCtrl.createPageAndBlockRelativeToPage({
                relativeToPage: this.page,
                blockType: 'schedule_container_block',
                pageTitle: 'Scheduler',
                blockOptions: { shouldSelectBlock: false },
                applySideEffects: () => {
                    builderUiStore.setShowScheduleOverlayForId(null);
                    toastStore.add({
                        id: toastId,
                        type: ToastType.Info,
                        text: t('title'),
                        autoDismissMs: 8000,
                        actionLink: {
                            text: t('link'),
                            onClick: () =>
                                builderUiStore.selectAndScrollToBlock({
                                    block: this.flow.scheduleContainerBlock,
                                    focusBlock: true
                                })
                        }
                    });
                },
                revertSideEffects: () => {
                    toastStore.remove(toastId);
                    if (!builderUiStore.selectedPage) {
                        builderUiStore.selectPage(this.page_id);
                    }
                }
            });
        }
    }

    getDataForServer(): ServiceSelectionBlockDataForServer {
        return {
            ...super.getDataForServer(),
            type: this.type as 'service_selection_block',
            invoice_block_id: this.invoice_block_id,
            selection_required: this.selection_required,
            send_info_to_invoice: this.send_info_to_invoice,
            selection_type: this.selection_type,
            show_total: this.show_total,
            discount: this.discount,
            services_layout: this.services_layout,
            banner_text: this.banner_text,
            service_selection_type: this.service_selection_type
        };
    }
}
